import React, {useEffect, useState, useRef} from 'react';
import ArrAccordionWithCheckbox from '../../common/arr-accordion-with-checkbox/ArrAccordionWithCheckbox';
import FilterHeader from './FilterHeader';
import {
  useGenerateClusterSummaryReport,
  useGetExplorer,
  useConceptDocList,
} from '../../../hooks/conceptExplorere/useConceptExplorer';
import Box from '@mui/material/Box';
import {Grid, Link, Typography} from '@mui/material';
import {t} from 'i18next';
import {
  ToastType,
  SourceTypeCode,
} from '../../../shared/constants';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {ReactComponent as UploadIcon} from '../../../assets/images/upload.svg';
import './ConceptExplorer.scss';
import HeatedMapAndPanel from './heated-map-panel/Index';
import NoReport from '../../../pages/reports/communication-explorer/NoReport';
import Cart from './selected-results-panel/Cart';
import {IChild} from './heated-map-panel/TypesHeatMap';
import {
  GenerateClusterSummaryReportPayload,
  IConceptDocListRequest,
} from '../../../api/types/conceptExplorer.type';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrDocMaxLimitWarning from '../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import {ReportActionMenuOptionTypes} from '../../../shared/constants';
import {ToastMessageConfigType} from '../../../api/types/ingestionApi.type';
import {useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../redux/slice/docListSlice';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../redux/slice/docViewSlice';
import {clearDocViewState} from '../../document-viewer/docViewerUtils';
import {
  setReportDetails,
  clearReportBackNavigation,
} from '../../../redux/slice/comExplorerSlice';
import {RootState} from '../../../redux/store';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  communicationExpUrl,
  conceptExpUrl,
} from '../communication-explorer/utils';
import { CONCEPT_EXPLORER_TOOLTIP_URL } from '../../../config/env';

export type ReportNotificationType = {
  notificationId: number | string;
  reportId: number | string;
  generateConceptReportPayload: GenerateClusterSummaryReportPayload['generateConceptReportPayload'];
};

export type ToastNotificationType = {
  show: boolean;
  type: ToastType[];
  message: {from: 'server' | 'client' | ''; text: string};
};

export const initialToast: ToastNotificationType = {
  show: false,
  type: [],
  message: {from: '', text: ''},
};

const ConceptExplorer = () => {
  const storedCurrentLevelIndex = useSelector(
    (state: any) => state.comExplorer?.renderJSONPayload?.currentLevelIndex
  );
  const [_reqData, setReqData] = useState<ReportNotificationType | null>(null);
  const [explorerDataList, setexplorerDataList] = useState<any>(null);
  const {mutate: ExplorerViewData} = useGetExplorer();
  const [cartItems, setCartItems] = useState<IChild[]>([]);
  const [toastNotification, setToastNotification] =
    useState<ToastNotificationType>(initialToast);
  const [searchParams] = useSearchParams();
  const reportIDFromUrl = Number(searchParams.get('ReportID'));
  const {
    shoppingCartValue,
    backSourceLabel,
    backSourcePath,
    sourceId: communicationSourceId,
  } = useSelector((state: RootState) => state.comExplorer);

  // Use ref to track current _reqData to avoid closure issues
  const reqDataRef = useRef<ReportNotificationType | null>(null);
  const cartItemsRef = useRef<IChild[]>([]);
  useEffect(() => {
    reqDataRef.current = _reqData;
  }, [_reqData]);
  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  useEffect(() => {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (
      shoppingCartValue &&
      currentPath.toLowerCase().includes(conceptExpUrl.toLowerCase())
    ) {
      setCartItems(shoppingCartValue);
      dispatch(clearReportBackNavigation());
    }
  }, [shoppingCartValue]);

  const [currentLevelIndex, setCurrentLevelIndex] = useState<number>(
    storedCurrentLevelIndex ?? 0
  );
  const [selectedCluster, setSelectedCluster] = useState<{
    [key: number]: string | null;
  }>({0: null, 1: null, 2: null});
  const [isNavigatingToDocList, setIsNavigatingToDocList] =
    useState<boolean>(false);
  const [isNavigatingToDocView, setIsNavigatingToDocView] =
    useState<boolean>(false);
  const [showDocLimitWarning, setShowDocLimitWarning] =
    useState<boolean>(false);
  const [navigationType, setNavigationType] = useState<SourceTypeCode>();
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  // Store callback from ActionMenuWrapper for navigation actions
  const navigationCallbackRef = useRef<
    ((docId: string, source: string) => void) | undefined
  >(undefined);

  // Use refs to track currentLevelIndex and selectedCluster to avoid closure issues
  const currentLevelIndexRef = useRef<number>(0);
  const selectedClusterRef = useRef<{[key: number]: string | null}>({
    0: null,
    1: null,
    2: null,
  });
  useEffect(() => {
    currentLevelIndexRef.current = currentLevelIndex;
  }, [currentLevelIndex]);
  useEffect(() => {
    selectedClusterRef.current = selectedCluster;
  }, [selectedCluster]);
  const {mutate: postGenerateClusterSummaryReport, isPending} =
    useGenerateClusterSummaryReport();
  // Navigation hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Hook for concept-doclist API
  const {mutate: createConceptDocList, isPending: isConceptDocListLoading} =
    useConceptDocList();

  const requestPayload = {
    reportID: reportIDFromUrl ? String(reportIDFromUrl) : '',
    docConceptReportID: '',
    pageSource: '',
    previousUrl: '',
    searchId: '',
    originalSourceReportId: '',
    source: '',
    navigationSourceType: '',
    reportMasterDoclistId: '',
    redirectionData: null,
    redirectionDcos: [
      {
        id: '',
        text: '',
      },
    ],
  };

  const GetInitialData = () => {
    ExplorerViewData(requestPayload, {
      onSuccess: (data) => {
        setexplorerDataList(data);
      },
      onError: () => {},
    });
  };
  useEffect(() => {
    if (reportIDFromUrl) GetInitialData();
  }, [reportIDFromUrl]);

  const handleMenuAction = (actionId: string) => {
    if (actionId === 'tally') {
      // Validation
      const currentReqData = reqDataRef.current;
      const currentCartItems = cartItemsRef.current;

      // Check if we have reportId from reqData or from URL
      const hasReportId =
        (currentReqData && currentReqData.reportId) || reportIDFromUrl;

      if (!hasReportId) {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'client',
            text: 'Please apply filters first to generate a report',
          },
        });
        return;
      }

      if (currentCartItems.length === 0) {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'client',
            text: 'Please select at least one cluster from the heat map',
          },
        });
        return;
      }

      // Build payload for DOCLIST to get docListId
      const payload = buildConceptDocListPayload('DOCLIST');
      if (!payload) {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'client',
            text: 'Unable to build request payload',
          },
        });
        return;
      }

      // Call API to get docListId
      createConceptDocList(payload, {
        onSuccess: (docListId) => {
          // Response is directly a number (e.g., 320)
          if (!docListId || docListId === 0) {
            setToastNotification({
              show: true,
              type: [ToastType.Error],
              message: {
                from: 'client',
                text: 'Invalid document list ID received from server',
              },
            });
            return;
          }

          // Call ActionMenuWrapper's callback for tally navigation
          // The callback signature is (docId: string, navigationSourceType: string, sourceName?: string)
          // but TypeScript sees it as (docId: string, source: string)
          if (tallyCallbackRef.current) {
            // Cast to call with 3 parameters - runtime supports it
            (tallyCallbackRef.current as any)(
              String(docListId),
              'CONCEPTEXPLORER',
              'DOCLIST'
            );
            tallyCallbackRef.current = undefined;
          } else {
            // Fallback: navigate directly if callback is not available (backward compatibility)
            navigate('/Report/Tally', {
              state: {
                navigationSourceType: 'CONCEPTEXPLORER',
                source: [String(docListId)],
                sourceName: 'DOCLIST',
              },
            });
          }
        },
        onError: () => {
          setToastNotification({
            show: true,
            type: [ToastType.Error],
            message: {
              from: 'client',
              text: 'Failed to get document list ID',
            },
          });
        },
      });
    }
  };

  useEffect(() => {
    GetInitialData();
    return () => {
      //clear store only if navigating to any other page than communication explorer or concept explorer
      const currentUrl =
        `${window.location.pathname}${window.location.search}`.toLowerCase();

      if (
        currentUrl.includes(communicationExpUrl.toLowerCase()) ||
        currentUrl.includes(conceptExpUrl.toLowerCase())
      )
        return;

      if (communicationSourceId) {
        dispatch(clearReportBackNavigation());
      }
    };
  }, []);

  const resetSourceIds = () => {
    const sourceIDArray = explorerDataList?.conceptExplorerData?.sourceID;
    if (Array.isArray(sourceIDArray) && sourceIDArray.length > 0) {
      if (explorerDataList && explorerDataList.conceptExplorerData) {
        setexplorerDataList((prev: any) => ({
          ...prev,
          conceptExplorerData: null,
        }));
      }
    }
  };

  // Helper function to get document count from cart items
  const getDocumentCount = (): number => {
    // Use ref to get current cartItems value to avoid closure issues
    return cartItemsRef.current.reduce((sum, item) => sum + item.size, 0);
  };

  // Helper function to build ConceptExplorer source details as object
  // Accepts optional parameters to use state values directly (for useMemo) or falls back to refs (for callbacks)
  const buildConceptExplorerSourceDetailsObject = (
    cartItemsParam?: IChild[],
    currentLevelIndexParam?: number
  ) => {
    // Use ref to get current value to avoid closure issues
    const currentReqData = reqDataRef.current;

    // Use reportId from reqData or fallback to reportIDFromUrl
    const reportIdToUse = currentReqData?.reportId || reportIDFromUrl || null;

    if (!reportIdToUse) {
      return null;
    }

    const sourceName =
      currentReqData?.generateConceptReportPayload?.sourceName || '';
    const source = currentReqData?.generateConceptReportPayload?.source || [];
    const filters = currentReqData?.generateConceptReportPayload?.filters || [];
    // Convert reportId to integer
    const reportId =
      typeof reportIdToUse === 'number'
        ? reportIdToUse
        : typeof reportIdToUse === 'string'
          ? Number(reportIdToUse) || 0
          : 0;

    // Use provided parameters (from state) if available, otherwise use refs (for callbacks)
    // This ensures useMemo gets fresh values when state changes
    const currentCartItems =
      cartItemsParam !== undefined ? cartItemsParam : cartItemsRef.current;
    const currentLevel =
      currentLevelIndexParam !== undefined
        ? currentLevelIndexParam
        : currentLevelIndexRef.current;

    const shoppingCardClusterId = currentCartItems.map((item) => ({
      ClusterID: typeof item.id === 'number' ? item.id : Number(item.id) || 0,
      Text: item.name,
      Level: currentLevel + 1,
    }));

    // Get selected cluster ID from last selected cart item as integer
    const selectedClusterId =
      currentCartItems.length > 0
        ? typeof currentCartItems[currentCartItems.length - 1].id === 'number'
          ? currentCartItems[currentCartItems.length - 1].id
          : Number(currentCartItems[currentCartItems.length - 1].id) || 0
        : 0;

    const sourceDetails = {
      SourceName: sourceName,
      Source: source,
      Filters: filters,
      ReportID: reportId,
      ShoppingCardclusterId: shoppingCardClusterId,
      ClusterText: '',
      SelectedClusterId: selectedClusterId,
    };

    return sourceDetails;
  };

  // Helper function to build ConceptExplorer source details payload as string
  const buildConceptExplorerSourceDetails = (): string => {
    const sourceDetails = buildConceptExplorerSourceDetailsObject();
    return sourceDetails ? JSON.stringify(sourceDetails) : '';
  };

  // Helper function to build payload for concept-doclist API
  const buildConceptDocListPayload = (
    navigatingTo: 'DOCLIST' | 'DOCVIEW' | 'COMMUNICATIONMAP' = 'DOCLIST',
    totalDocumentToView?: number
  ): IConceptDocListRequest | null => {
    const currentReqData = reqDataRef.current;
    const currentCartItems = cartItemsRef.current;
    const currentLevel = currentLevelIndexRef.current;

    // Use reportId from reqData or fallback to reportIDFromUrl
    const reportIdToUse = currentReqData?.reportId || reportIDFromUrl || null;

    if (!reportIdToUse) {
      return null;
    }

    const reportId =
      typeof reportIdToUse === 'number'
        ? reportIdToUse
        : typeof reportIdToUse === 'string'
          ? Number(reportIdToUse) || 0
          : 0;

    const sourceName =
      currentReqData?.generateConceptReportPayload?.sourceName || '';

    const shoppingCardclusterId = currentCartItems.map((item) => ({
      clusterID: typeof item.id === 'number' ? item.id : Number(item.id) || 0,
      level: currentLevel + 1,
      text: item.name,
    }));

    // Calculate actual document count
    const actualDocumentCount = getDocumentCount();
    // Use provided totalDocumentToView or fallback to actual count
    const finalTotalDocumentToView = totalDocumentToView ?? actualDocumentCount;
    // Determine if threshold flag should be false (capped) or true (not capped)
    // If totalDocumentToView is less than actual count, it means it's capped
    const isCapped = finalTotalDocumentToView < actualDocumentCount;

    return {
      sourceName,
      reportId,
      shoppingCardclusterId,
      navigatingTo,
      threasholdFlag: navigatingTo === 'COMMUNICATIONMAP' ? false : !isCapped,
      totalDocumentToView: finalTotalDocumentToView,
      includeClusterlessDocuments: false,
      includeBottom30Clusters: false,
      conceptData: {
        conceptExplorerReportID: '',
        searchId: '',
        clusterId: '',
        actionType: '',
        vizLevel: '',
        clusterText: '',
        selectedClusterID: '',
      },
    };
  };

  // Handler for ViewInDocList action
  const handleViewInDocList = async (totalDocumentToView?: number) => {
    // Validation
    const currentReqData = reqDataRef.current;
    const currentCartItems = cartItemsRef.current;

    // Check if we have reportId from reqData or from URL
    const hasReportId =
      (currentReqData && currentReqData.reportId) || reportIDFromUrl;

    if (!hasReportId) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please apply filters first to generate a report',
        },
      });
      return;
    }

    if (currentCartItems.length === 0) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please select at least one cluster from the heat map',
        },
      });
      return;
    }

    // Build payload for DOCLIST with totalDocumentToView (may be capped by limit)
    const payload = buildConceptDocListPayload('DOCLIST', totalDocumentToView);
    if (!payload) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Unable to build request payload',
        },
      });
      return;
    }

    // Set loading state
    setIsNavigatingToDocList(true);
    setShowDocLimitWarning(false);

    // Call API
    createConceptDocList(payload, {
      onSuccess: (docListId) => {
        // Response is directly a number (e.g., 320)
        if (!docListId || docListId === 0) {
          setToastNotification({
            show: true,
            type: [ToastType.Error],
            message: {
              from: 'client',
              text: 'Invalid document list ID received from server',
            },
          });
          setIsNavigatingToDocList(false);
          return;
        }

        // Store in Redux
        clearDocViewState();
        dispatch(setDocListId(docListId));
        dispatch(storeDocumentListMasterId(String(docListId)));
        dispatch(storeSourceTypeCode(SourceTypeCode.CONCEPTEXPLORER));

        // Store back navigation info
        const backPath = `${window.location.pathname}${window.location.search}`;
        dispatch(
          setDocListBackNavigation({
            label: t('reports.dataAnalysisReport.conceptExplorer.title'),
            path: backPath,
          })
        );

        // Call ActionMenuWrapper's callback to update its internal state
        // This ensures ActionMenuWrapper's Redux state is in sync
        if (navigationCallbackRef.current) {
          navigationCallbackRef.current(
            String(docListId),
            SourceTypeCode.CONCEPTEXPLORER
          );
          navigationCallbackRef.current = undefined;
        }

        // Navigate immediately after storing in Redux (don't wait for SignalR)
        setIsNavigatingToDocList(false);
        setTimeout(() => {
          dispatch(
            setReportDetails({
              backSourceLabel: '',
              sourceId: docListId.toString(),
              backSourcePath: backPath,
              shoppingCartValue: cartItems,
            })
          );
          navigate('/Document/DocList');
        }, 1000);
      },
      onError: (error) => {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'server',
            text: error?.message || 'Error creating document list',
          },
        });
        setIsNavigatingToDocList(false);
      },
    });
  };

  // Handler for ViewInDocView action
  const handleViewInDocView = async (totalDocumentToView?: number) => {
    // Validation
    const currentReqData = reqDataRef.current;
    const currentCartItems = cartItemsRef.current;

    // Check if we have reportId from reqData or from URL
    const hasReportId =
      (currentReqData && currentReqData.reportId) || reportIDFromUrl;

    if (!hasReportId) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please apply filters first to generate a report',
        },
      });
      return;
    }

    if (currentCartItems.length === 0) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please select at least one cluster from the heat map',
        },
      });
      return;
    }

    // Build payload for DOCVIEW with totalDocumentToView (may be capped by limit)
    const payload = buildConceptDocListPayload('DOCVIEW', totalDocumentToView);
    if (!payload) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Unable to build request payload',
        },
      });
      return;
    }

    // Set loading state
    setIsNavigatingToDocView(true);
    setShowDocLimitWarning(false);

    // Call API
    createConceptDocList(payload, {
      onSuccess: (docListId) => {
        // Response is directly a number (e.g., 320)
        if (!docListId || docListId === 0) {
          setToastNotification({
            show: true,
            type: [ToastType.Error],
            message: {
              from: 'client',
              text: 'Invalid document list ID received from server',
            },
          });
          setIsNavigatingToDocView(false);
          return;
        }

        // Store in Redux
        clearDocViewState();
        dispatch(storeDocumentListMasterId(String(docListId)));
        dispatch(storeSourceTypeCode(SourceTypeCode.CONCEPTEXPLORER));

        // Call ActionMenuWrapper's callback to update its internal state
        // This ensures ActionMenuWrapper's Redux state is in sync
        if (navigationCallbackRef.current) {
          navigationCallbackRef.current(
            String(docListId),
            SourceTypeCode.CONCEPTEXPLORER
          );
          navigationCallbackRef.current = undefined;
        }

        // Navigate immediately after storing in Redux (don't wait for SignalR)
        setIsNavigatingToDocView(false);
        setTimeout(() => {
          navigate('/DocumentViewer/DocView');
        }, 1000);
      },
      onError: (error) => {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'server',
            text: error?.message || 'Error creating document list',
          },
        });
        setIsNavigatingToDocView(false);
      },
    });
  };

  // Handler for ViewInDocView in new tab action
  const handleViewInDocViewNewTab = async (totalDocumentToView?: number) => {
    // Validation
    const currentReqData = reqDataRef.current;
    const currentCartItems = cartItemsRef.current;

    // Check if we have reportId from reqData or from URL
    const hasReportId =
      (currentReqData && currentReqData.reportId) || reportIDFromUrl;

    if (!hasReportId) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please apply filters first to generate a report',
        },
      });
      return;
    }

    if (currentCartItems.length === 0) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please select at least one cluster from the heat map',
        },
      });
      return;
    }

    // Build payload for DOCVIEW with totalDocumentToView (may be capped by limit)
    const payload = buildConceptDocListPayload('DOCVIEW', totalDocumentToView);
    if (!payload) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Unable to build request payload',
        },
      });
      return;
    }

    // Set loading state
    setIsNavigatingToDocView(true);
    setShowDocLimitWarning(false);

    // Call API
    createConceptDocList(payload, {
      onSuccess: (docListId) => {
        // Response is directly a number (e.g., 320)
        if (!docListId || docListId === 0) {
          setToastNotification({
            show: true,
            type: [ToastType.Error],
            message: {
              from: 'client',
              text: 'Invalid document list ID received from server',
            },
          });
          setIsNavigatingToDocView(false);
          return;
        }

        // Store in Redux for the new tab to access
        clearDocViewState();
        dispatch(storeDocumentListMasterId(String(docListId)));
        dispatch(storeSourceTypeCode(SourceTypeCode.CONCEPTEXPLORER));

        // Call ActionMenuWrapper's callback to update its internal state
        // This ensures ActionMenuWrapper's Redux state is in sync
        if (navigationCallbackRef.current) {
          navigationCallbackRef.current(
            String(docListId),
            SourceTypeCode.CONCEPTEXPLORER
          );
          navigationCallbackRef.current = undefined;
        }

        // Open in new tab immediately (don't wait for SignalR)
        setIsNavigatingToDocView(false);
        setTimeout(() => {
          const newTab = window.open('/DocumentViewer/DocView', '_blank');
          if (!newTab) {
            setToastNotification({
              show: true,
              type: [ToastType.Error],
              message: {
                from: 'client',
                text: 'Unable to open new tab. Please check your browser settings.',
              },
            });
          }
        }, 1000);
      },
      onError: (error) => {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'server',
            text: error?.message || 'Error creating document list',
          },
        });
        setIsNavigatingToDocView(false);
      },
    });
  };

  // Helper function to start document navigation with potentially capped document count
  const startDocumentNavigation = (
    actionId: string,
    totalDocumentToView: number
  ) => {
    if (actionId === 'view_doc_list') {
      handleViewInDocList(totalDocumentToView);
    } else if (actionId === 'view_doc_view') {
      handleViewInDocView(totalDocumentToView);
    } else if (actionId === 'view_doc_view_new_tab') {
      handleViewInDocViewNewTab(totalDocumentToView);
    }
  };

  // Action select handler - only handles navigation actions
  const onActionSelect = (actionId: string) => {
    // Handle ViewInDocList action
    if (actionId === 'view_doc_list') {
      setNavigationType(SourceTypeCode.DOCLIST);
      setSelectedActionId(actionId);
      setShowDocLimitWarning(true);
      return;
    }

    // Handle ViewInDocView action
    if (actionId === 'view_doc_view') {
      setNavigationType(SourceTypeCode.DOCVIEW);
      setSelectedActionId(actionId);
      setShowDocLimitWarning(true);
      return;
    }

    // Handle ViewInDocView in new tab action
    if (actionId === 'view_doc_view_new_tab') {
      setNavigationType(SourceTypeCode.DOCVIEW);
      setSelectedActionId(actionId);
      setShowDocLimitWarning(true);
      return;
    }
    if (actionId == 'comm_explorer') {
      handleAnalyzeComExplorer();
      return;
    }
  };

  const handleAnalyzeComExplorer = () => {
    const docListPayload = buildConceptDocListPayload('COMMUNICATIONMAP');
    if (!docListPayload) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Unable to build request payload',
        },
      });
      return;
    }
    createConceptDocList(docListPayload, {
      onSuccess: (docListId) => {
        if (!docListId || docListId === 0) {
          setToastNotification({
            show: true,
            type: [ToastType.Error],
            message: {
              from: 'client',
              text: 'Invalid document list ID received from server',
            },
          });
          return;
        } else {
          const backPath = `${window.location.pathname}${window.location.search}`;
          dispatch(
            setReportDetails({
              backSourceLabel: 'Concept Explorer',
              sourceId: docListId.toString(),
              backSourcePath: backPath,
              shoppingCartValue: cartItems,
              navigationSourceType: 'SESSIONSEARCH',
              sourceName: 'DOCLIST',
              sourceSelectionText: t(
                'reports.communicationExplorer.fromConceptExplorer'
              ),
            })
          );
          navigate('/DataAnalysisReport/CommunicationExplorerReport');
        }
      },
      onError: () => {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'client',
            text: 'Failed to get document list ID',
          },
        });
      },
    });
  };
  // Get source details for ActionMenuWrapper
  // Pass state values directly to ensure fresh data when cartItems or currentLevelIndex changes
  const getSourceDetailsForActionMenu = useMemo(() => {
    return buildConceptExplorerSourceDetailsObject(
      cartItems,
      currentLevelIndex
    );
  }, [_reqData, cartItems, currentLevelIndex]);

  // Customize doAction payload for ConceptExplorer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getDoActionData = (data: {
    operationType?: string;
    sourceDetails?: {[key: string]: any};
  }) => {
    return {
      selectedList: undefined, // Use undefined instead of null to match type
      destinationList: {},
    };
  };

  // Get source details string for API calls (used by ActionMenuWrapper internally)
  const getSourceDetails = (
    sourceDetails: {[key: string]: any},
    forAPI?: string
  ) => {
    if (forAPI === 'getBulkDocListId') {
      return buildConceptExplorerSourceDetailsObject() || {};
    }
    return sourceDetails;
  };

  // Store callback from ActionMenuWrapper for tally action
  // The callback signature matches (docId: string, source: string) but we use source as navigationSourceType
  // and can accept an optional third parameter for sourceName
  const tallyCallbackRef = useRef<
    ((docId: string, source: string, ...args: any[]) => void) | undefined
  >(undefined);

  // Handle action pick from ActionMenuWrapper
  const handleActionPick = (
    action: CustomMenuItem,
    callBackForDoc?: (docId: string, source: string) => void
  ) => {
    const actionId = action.id as ReportActionMenuOptionTypes | string;

    // Handle tally action
    if (
      actionId === ReportActionMenuOptionTypes.Tally ||
      actionId === 'tally'
    ) {
      // Store the callback to call after API success
      // ActionMenuWrapper provides callback with signature (docId, navigationSourceType, sourceName?)
      // but TypeScript sees it as (docId, source) - we'll cast it when calling
      tallyCallbackRef.current = callBackForDoc;
      // Use handleMenuAction to trigger tally flow
      handleMenuAction('tally');
      return;
    }

    // Handle navigation actions
    if (
      actionId === ReportActionMenuOptionTypes.ViewInDocList ||
      actionId === ReportActionMenuOptionTypes.ViewInDocView ||
      actionId === ReportActionMenuOptionTypes.ViewInDocViewNewTab
    ) {
      // Store the callback to call after API success
      navigationCallbackRef.current = callBackForDoc;

      if (actionId === ReportActionMenuOptionTypes.ViewInDocList) {
        setNavigationType(SourceTypeCode.DOCLIST);
        setSelectedActionId('view_doc_list');
      } else if (actionId === ReportActionMenuOptionTypes.ViewInDocView) {
        setNavigationType(SourceTypeCode.DOCVIEW);
        setSelectedActionId('view_doc_view');
      } else if (actionId === ReportActionMenuOptionTypes.ViewInDocViewNewTab) {
        setNavigationType(SourceTypeCode.DOCVIEW);
        setSelectedActionId('view_doc_view_new_tab');
      }
      setShowDocLimitWarning(true);
    }
  };

  // Show toast from ActionMenuWrapper
  const showToastFromParent = (config: ToastMessageConfigType) => {
    setToastNotification({
      show: true,
      type: [config.type ?? ToastType.Success],
      message: {
        from: 'server',
        text: config.message ?? '',
      },
    });
  };

  // Validate before allowing bulk actions
  // Note: Use current state (cartItems) instead of ref to avoid stale data issues
  const validateBeforeBulkAction = (): boolean => {
    const currentReqData = reqDataRef.current;
    // Use current cartItems state instead of ref to get the latest value
    const currentCartItems = cartItems;

    const hasReportId =
      (currentReqData && currentReqData.reportId) || reportIDFromUrl;

    if (!hasReportId) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please apply filters first to generate a report',
        },
      });
      return false;
    }

    if (currentCartItems.length === 0) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Please select at least one cluster from the heat map',
        },
      });
      return false;
    }

    const documentCount = getDocumentCount();
    if (documentCount === 0) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'No documents selected',
        },
      });
      return false;
    }

    const sourceDetails = buildConceptExplorerSourceDetails();
    if (!sourceDetails || sourceDetails === '') {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'Unable to build source details. Please try applying filters again.',
        },
      });
      return false;
    }

    return true;
  };

  const sections = [
    {
      id: 'filterHeader',
      title: {
        primaryText: 'Select Sources & Set Display Controls',
        secondaryText: '',
      },
      content: (
        <FilterHeader
          setReqData={setReqData}
          explorerData={explorerDataList}
          setCartItems={setCartItems}
          setToastNotification={setToastNotification}
          onClearSourceId={resetSourceIds}
        />
      ),
      checked: false,
      showCheckbox: false,
    },
  ];

  const handleGenerateReport = () => {
    if (!_reqData) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: 'applyFiltersErrorMsg',
        },
      });
      return;
    }
    if (toastNotification.message.from === 'server') {
      setToastNotification((prev) => ({...prev, show: true}));
      return;
    }
    postGenerateClusterSummaryReport(_reqData?.generateConceptReportPayload, {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onSuccess: (data = null) => {
        setToastNotification({
          show: true,
          type: [ToastType.Success],
          message: {
            from: 'server',
            text: t('reports.common.reportExportSuccess'),
          },
        });
      },
      onError: () => {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'server',
            text: t('reports.common.reportExportError'),
          },
        });
      },
    });
  };

  const handleBackNavigation = () => {
    if (backSourcePath) navigate(backSourcePath);
    else navigate(-1);
  };

  const backButtonText = backSourceLabel
    ? t('reports.dataAnalysisReport.conceptExplorer.backButtonToSource', {
        source: backSourceLabel,
      })
    : t('reports.dataAnalysisReport.conceptExplorer.backButton');

  return (
    <>
      {toastNotification.show && (
        <ArrToastNotification
          message={
            toastNotification.type[0] === 'error'
              ? t(
                  `reports.dataAnalysisReport.conceptExplorer.${toastNotification.message.text}`
                )
              : toastNotification.message.text
          }
          status={toastNotification.type}
          onClose={() => {
            setToastNotification((prev) => ({...prev, show: false}));
          }}
        />
      )}
      <Box className="concept-explorer-container communication-explorer-container">
        <Grid container>
          <Grid size={{xs: 9}}>
            <div
              className="concept-explorer-header"
              data-testid={`concept-explorer-title-paragraph1-paragraph2-domain-url-paragraph3-paragraph4-paragraph5-paragraph6`}>
              <Typography
                className="typo-text headline2_size concept-explorer-header-title"
                tabIndex={0}
                data-testid={`concept-explorer-title`}>
                {t('reports.dataAnalysisReport.conceptExplorer.title')}
              </Typography>
              <ArrTooltipWrapper
                title={t('reports.dataAnalysisReport.conceptExplorer.title')}
                trigger={
                  <InfoIcon
                    className="concept-explorer-info-icon"
                    fontSize="medium"
                    tabIndex={0}
                    data-testid="concept-explorer-info-icon"
                    aria-label={t(
                      'reports.dataAnalysisReport.conceptExplorer.tooltip.icon'
                    )}
                  />
                }
              >
                <div data-testid="ceh-tooltip-content" tabIndex={0}>
                  <p data-testid={`concept-explorer-paragraph1`}>
                    {t(
                      'reports.dataAnalysisReport.conceptExplorer.tooltip.paragraph1'
                    )}
                  </p>
                  <p data-testid={`concept-explorer-paragraph2-domain-url`}>
                    {t(
                      'reports.dataAnalysisReport.conceptExplorer.tooltip.paragraph2'
                    )}
                    <Link href={CONCEPT_EXPLORER_TOOLTIP_URL} target="_blank">
                      {t(
                        'reports.dataAnalysisReport.conceptExplorer.tooltip.domainUrl'
                      )}
                    </Link>
                  </p>
                  <p data-testid={`concept-explorer-paragraph3`}>
                    {t(
                      'reports.dataAnalysisReport.conceptExplorer.tooltip.paragraph3'
                    )}
                  </p>
                  <p data-testid={`concept-explorer-paragraph4`}>
                    {t(
                      'reports.dataAnalysisReport.conceptExplorer.tooltip.paragraph4'
                    )}
                  </p>
                  <p data-testid={`concept-explorer-paragraph5`}>
                    {t(
                      'reports.dataAnalysisReport.conceptExplorer.tooltip.paragraph5'
                    )}
                  </p>
                  <p data-testid={`concept-explorer-paragraph6`}>
                    {t(
                      'reports.dataAnalysisReport.conceptExplorer.tooltip.paragraph6'
                    )}
                  </p>
                </div>
              </ArrTooltipWrapper>
              {backSourcePath && (
                <Box>
                  <ArrButton
                    variant="text"
                    key="back to source name"
                    size="small"
                    onClick={handleBackNavigation}
                    dataTestId="concept-explorer-button">
                    <ArrowBackIcon fontSize="small" />
                    {backButtonText}
                  </ArrButton>
                </Box>
              )}
              <ArrIconButton
                className="concept-explorer-upload-icon"
                icon={<UploadIcon />}
                tabIndex={0}
                data-testid="concept-explorer-upload-icon"
                ariaLabel={t(
                  'reports.dataAnalysisReport.conceptExplorer.exportIcon'
                )}
                size="large"
                onClick={handleGenerateReport}
                loading={isPending}
                testId="concept-explorer-export-icon-icon-button" />
            </div>
            <Box className="communication-explorer-container">
              <ArrAccordionWithCheckbox
                expandedSection={'filterHeader'}
                sections={sections}
              />
              {!_reqData?.reportId && !reportIDFromUrl ? (
                <NoReport />
              ) : (
                <HeatedMapAndPanel
                  reqData={_reqData}
                  cartItems={cartItems}
                  setCartItems={setCartItems}
                  toastNotification={toastNotification}
                  setToastNotification={setToastNotification}
                  currentLevelIndex={currentLevelIndex}
                  setCurrentLevelIndex={setCurrentLevelIndex}
                  selectedCluster={selectedCluster}
                  setSelectedCluster={setSelectedCluster}
                />
              )}
            </Box>
          </Grid>
          <Grid size={{xs: 3}} className="cart-container">
            <Cart
              cartItems={cartItems}
              setCartItems={setCartItems}
              onMenuAction={handleMenuAction}
              onActionSelect={onActionSelect}
              sourceDetails={getSourceDetailsForActionMenu || undefined}
              getDoActionData={getDoActionData}
              getSourceDetails={getSourceDetails}
              showToastFromParent={showToastFromParent}
              onActionPick={handleActionPick}
              validateBeforeBulkAction={validateBeforeBulkAction}
            />
          </Grid>
        </Grid>
      </Box>
      {/* Loading Spinner */}
      {(isConceptDocListLoading ||
        isNavigatingToDocList ||
        isNavigatingToDocView) && (
        <ArrLoadingSpinner
          overlay
          isOpen={
            isConceptDocListLoading ||
            isNavigatingToDocList ||
            isNavigatingToDocView
          }
        />
      )}
      {/* Document Limit Warning Modal */}
      <ArrDocMaxLimitWarning
        totalDocumentCount={getDocumentCount()}
        show={showDocLimitWarning}
        destination={navigationType}
        onCancel={() => setShowDocLimitWarning(false)}
        onContinue={(totalDocumentToView: number) =>
          startDocumentNavigation(selectedActionId, totalDocumentToView)
        }
      />
    </>
  );
};

export default ConceptExplorer;
